import { ShieldCheckIcon } from 'lucide-react';
import type { ComponentType } from 'react';

import { useHasPermission } from '@/shared/auth/hooks';
import { ADMIN_AREA_PERMISSIONS, PERMISSIONS } from '@/shared/auth/permissions';
import { defineModule } from '@/shared/registry/types';

import { adminKeys, SETTINGS_UPDATED, useMessagingSummary, type PublicPortalSettings } from './api';

/**
 * Sidebar counter: messages waiting in error queues or parked outboxes. Only people who may open the messaging dashboard ask
 * for it (`admin.messaging.manage`), so nobody else's browser fires a request the server would refuse.
 */
const useProblemCounter = (): number | undefined => {
  const canSee = useHasPermission(PERMISSIONS.admin.messagingManage);
  const summary = useMessagingSummary(canSee);
  return canSee && summary.data ? summary.data.errorMessages + summary.data.parkedOutboxMessages : undefined;
};

const page = (load: () => Promise<{ default: ComponentType }>) => async () => ({
  Component: (await load()).default,
});

function isPublicSettings(value: unknown): value is PublicPortalSettings {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { companyName?: unknown }).companyName === 'string'
  );
}

/**
 * `admin` module: the administration area (docs/modules/admin.md) — portal settings (name, logo, accent, language,
 * time zone), system health, the recurring jobs with "Run now", alerts, the infrastructure settings (SMTP, web push, rate
 * limits, thresholds), the messaging dashboard with error queues, the search index, the cleanup of unattached
 * uploads (docs/modules/files.md) and the roles with their permissions (`/admin/roles`) — for administrators and for the people whose
 * roles hold the permission of a section: each person sees the tabs they may use (`access.ts`).
 * Users, invitations and departments are managed in the company module (`/company/admin/…`), linked from the overview.
 */
export const module = defineModule({
  id: 'admin',
  routes: [
    {
      path: 'admin',
      lazy: page(() => import('./pages/AdminLayout')),
      children: [
        { index: true, lazy: page(() => import('./pages/OverviewPage')) },
        { path: 'settings', lazy: page(() => import('./pages/SettingsPage')) },
        { path: 'menu', lazy: page(() => import('./pages/MenuPage')) },
        { path: 'security', lazy: page(() => import('./pages/SecurityPage')) },
        { path: 'roles', lazy: page(() => import('./pages/RolesPage')) },
        { path: 'audit', lazy: page(() => import('./pages/AuditPage')) },
        { path: 'system', lazy: page(() => import('./pages/SystemPage')) },
        { path: 'jobs', lazy: page(() => import('./pages/JobsPage')) },
        { path: 'alerts', lazy: page(() => import('./pages/AlertsPage')) },
        { path: 'infrastructure', lazy: page(() => import('./pages/InfrastructurePage')) },
        { path: 'messaging', lazy: page(() => import('./pages/MessagingPage')) },
        { path: 'messaging/:endpoint', lazy: page(() => import('./pages/ErrorQueuePage')) },
        { path: 'search', lazy: page(() => import('./pages/SearchIndexPage')) },
        { path: 'files', lazy: page(() => import('./pages/FilesCleanupPage')) },
        { path: 'disk', lazy: page(() => import('./pages/DiskPage')) },
        { path: 'history', lazy: page(() => import('./pages/HistoryPage')) },
      ],
    },
  ],
  nav: [
    {
      id: 'admin',
      labelKey: 'admin:nav.title',
      icon: ShieldCheckIcon,
      to: '/admin',
      // The area opens for anyone holding the permission of one of its sections (access.ts); administrators hold them all.
      permissions: ADMIN_AREA_PERMISSIONS,
      order: 10,
      section: 'admin',
      useCounter: useProblemCounter,
    },
  ],
  setup: ({ queryClient, realtime }) => {
    // Every client applies new portal settings at once (the shell reads the public settings query).
    realtime.on(SETTINGS_UPDATED, (payload) => {
      if (isPublicSettings(payload)) queryClient.setQueryData(adminKeys.publicSettings, payload);
      void queryClient.invalidateQueries({ queryKey: adminKeys.settings });
    });
  },
});
